import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ConfirmDeletionModal } from '../modal';

const STATUS_ACTIONS = [
  {
    status: 'PUBLISHED',
    label: 'Publish',
    variant: 'publish',
    icon: ['fas', 'paper-plane'],
  },
  {
    status: 'PREVIEW',
    label: 'Preview',
    variant: 'preview',
    icon: ['fas', 'eye'],
  },
  {
    status: 'UNPUBLISHED',
    label: 'Save Draft',
    variant: 'draft',
    icon: ['fas', 'floppy-disk'],
  },
  {
    status: 'DELETED',
    label: 'Delete',
    variant: 'delete',
    icon: ['fas', 'trash-can'],
  },
];

function statusHint(status) {
  switch (status) {
    case 'PUBLISHED':
      return 'This event is live and visible to the public.';
    case 'PREVIEW':
      return 'Preview mode — test before going live.';
    case 'DELETED':
      return 'This event has been deleted.';
    case 'UNPUBLISHED':
    default:
      return 'Draft saved recently.';
  }
}

function statusButtonClass(variant, isActive) {
  return cn(
    'bethere-status-btn',
    isActive
      ? `bethere-status-btn--${variant}-active`
      : `bethere-status-btn--${variant}-outline`,
  );
}

export function FestivalEventStatusPanel({
  festivalId,
  festivalName,
  status = 'UNPUBLISHED',
  readOnly = false,
  isPending = false,
  onStatusChange,
}) {
  const currentStatus = status || 'UNPUBLISHED';
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleClick = (nextStatus) => {
    if (readOnly || isPending || nextStatus === currentStatus) {
      return;
    }
    if (nextStatus === 'DELETED') {
      setDeleteConfirmOpen(true);
      return;
    }
    onStatusChange(nextStatus);
  };

  const confirmDelete = () => {
    setDeleteConfirmOpen(false);
    onStatusChange('DELETED');
  };

  return (
    <section className="bethere-form-section">
      <h2 className="mb-4 text-2xl font-bold text-[#121212]">Event Status</h2>
      <p className="m-0 text-sm leading-relaxed text-[var(--muted-foreground)]">
        {statusHint(currentStatus)}
      </p>
      <div
        className="bethere-sidebar-actions"
        role="group"
        aria-label="Event status actions"
      >
        {STATUS_ACTIONS.map(({ status: st, label, variant, icon }) => {
          const isActive = currentStatus === st;
          return (
            <button
              key={st}
              type="button"
              className={statusButtonClass(variant, isActive)}
              aria-pressed={isActive}
              disabled={readOnly || isPending}
              onClick={() => handleClick(st)}
            >
              <FontAwesomeIcon
                icon={icon}
                className="text-[length:var(--bethere-f-p)]"
              />
              {label}
            </button>
          );
        })}
      </div>
      {festivalId && (
        <p className="mt-4 text-xs text-[var(--muted-foreground)]">
          <Link
            to={`/festival/${festivalId}/view`}
            className="font-bold text-[var(--bethere-primary)] hover:text-[var(--bethere-tertiary)]"
          >
            View published layout
          </Link>
        </p>
      )}

      <ConfirmDeletionModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        itemName={festivalName || 'this event'}
        isPending={isPending}
      />
    </section>
  );
}
